import * as React from 'react';

// @ts-expect-error - imported from .js/.jsx file
import {DownloadIcon} from 'shells/icon/svg/download-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EmailIcon} from 'shells/icon/svg/email-icon';
// @ts-expect-error - imported from .js/.jsx file
import {EyeIcon} from 'shells/icon/svg/eye-icon';
import {HtmlLinkIcon} from 'shells/icon/svg/html-link-icon';
// @ts-expect-error - imported from .js/.jsx file
import {ReplyAllIcon} from 'shells/icon/svg/reply-all-icon';
// @ts-expect-error - imported from .js/.jsx file
import {ReplyIcon} from 'shells/icon/svg/reply-icon';
// @ts-expect-error - imported from .js/.jsx file
import {TrashIcon} from 'shells/icon/svg/trash-icon';
import {TimelineEntryActionMenu} from 'shells/timeline/entry/toolbar/timeline-entry-action-menu';

import type {EmailFragmentSparse as Email, SessionUser} from 'nutshell-graphql-types';

// @ts-expect-error
import {appHistory} from '../../history';
import {addNotification} from '../../notifications/index';

import {AddBlocklistedEmailConfirmation} from '../core/email/add-blocklisted-email-confirmation';
import {DeleteEmailPopover} from '../core/email/delete-email-popover';
import {useUpdateEmailBlocklist} from '../core/email/graphql/use-update-email-blocklist';

import {getEntryTypeFromPayload} from './helpers';

type Props = {
    changeLogId: string;
    email: Email;
    onDeleteEmail: (emailId: string, allByAddress?: string | null | undefined) => any;
    onUpdateEmail: (arg1: string, arg2: boolean) => Promise<any>;
    onRetryFailedEmail: (arg1: string) => Promise<any>;
    isViewingAttachments: boolean;
    setIsViewingAttachments: (arg1: boolean) => void;
    onReply: () => void;
    onReplyAll: () => void;
    currentUser: SessionUser;
};

export function EmailEventActionMenu(props: Props) {
    const {
        changeLogId,
        email,
        onDeleteEmail,
        onUpdateEmail,
        onRetryFailedEmail,
        isViewingAttachments,
        setIsViewingAttachments,
        onReply,
        onReplyAll,
        currentUser,
    } = props;
    const [selectedAddress, setSelectedAddress] = React.useState<string | null | undefined>(null);
    const [isConfirmingDelete, setIsConfirmingDelete] = React.useState<boolean>(false);
    const [isConfirmingAddToBlocklist, setIsConfirmingAddToBlocklist] =
        React.useState<boolean>(false);
    const buttonRef = React.useRef(null);

    const isReplyable = !email.isFailed;
    const isShareable = email.isShareable;
    const isShared = email.isShared;
    const hasAttachments = email.attachments.length;
    const isFailed = email.isFailed;

    const {onBlocklistEmail} = useUpdateEmailBlocklist();

    const actionMenuOptions = [];
    if (isReplyable) {
        actionMenuOptions.push({
            label: 'Reply',
            icon: <ReplyIcon size={15} wrapWithDiv={false} />,
            handleSelect: onReply,
        });
    }
    if (isReplyable && email.recipients.length > 1) {
        actionMenuOptions.push({
            label: 'Reply all',
            icon: <ReplyAllIcon size={15} wrapWithDiv={false} />,
            handleSelect: onReplyAll,
        });
    }
    actionMenuOptions.push({
        label: 'Copy link',
        icon: <HtmlLinkIcon size={15} wrapWithDiv={false} />,
        handleSelect: () => {
            navigator.clipboard.writeText(email.htmlUrl);
        },
    });
    if (isShareable && !isShared) {
        actionMenuOptions.push({
            label: 'Share with everyone',
            icon: <EyeIcon size={15} wrapWithDiv={false} />,
            handleSelect: () => onUpdateEmail(email.id, true),
        });
    }
    if (isShareable && isShared) {
        actionMenuOptions.push({
            label: 'Hide',
            icon: <EyeIcon size={15} wrapWithDiv={false} />,
            handleSelect: () => onUpdateEmail(email.id, false),
        });
    }
    if (hasAttachments) {
        actionMenuOptions.push({
            label: 'Download attachments',
            icon: <DownloadIcon size={15} wrapWithDiv={false} />,
            handleSelect: () => setIsViewingAttachments(true),
        });
    }
    if (isFailed) {
        actionMenuOptions.push({
            label: 'Retry sending',
            icon: <EmailIcon size={15} wrapWithDiv={false} />,
            handleSelect: () => onRetryFailedEmail(email.id),
            variant: 'destructive',
        });
    }

    actionMenuOptions.push({
        label: 'Delete',
        icon: <TrashIcon size={15} wrapWithDiv={false} />,
        handleSelect: () => setIsConfirmingDelete(true),
        variant: 'destructive',
    });

    return (
        <>
            <TimelineEntryActionMenu
                id={changeLogId}
                entryType={getEntryTypeFromPayload(email)}
                attachmentsToView={isViewingAttachments ? email.attachments : undefined}
                setIsViewingAttachments={setIsViewingAttachments}
                // @ts-expect-error
                customActionMenuOptions={actionMenuOptions}
                getButtonRef={buttonRef}
            />
            {isConfirmingDelete ? (
                <DeleteEmailPopover
                    anchor={buttonRef.current}
                    email={email}
                    currentUser={currentUser}
                    onCancel={() => {
                        setIsConfirmingDelete(false);
                    }}
                    onDeleteEmail={onDeleteEmail}
                    setSelectedAddress={setSelectedAddress}
                    selectedAddress={selectedAddress}
                    setIsConfirmingAddToBlocklist={setIsConfirmingAddToBlocklist}
                />
            ) : undefined}
            {isConfirmingAddToBlocklist && selectedAddress ? (
                <AddBlocklistedEmailConfirmation
                    email={email}
                    onCancel={() => {
                        setIsConfirmingDelete(false);
                    }}
                    onDeleteEmail={onDeleteEmail}
                    onBlocklistEmail={(address: string) =>
                        onBlocklistEmail({address}).then(() => {
                            addNotification('Add to blocklist', {
                                body: `${address} has been added to the company blocklist.`,
                                onClick: () => appHistory.push(`/setup/email-security`),
                            });
                        })
                    }
                    selectedAddress={selectedAddress}
                />
            ) : undefined}
        </>
    );
}
